/**
 * Sandboxed HTML embed for Work deliverables in chat.
 * Show the preview as soon as its HTML loads, without moving keyboard focus.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Linking, Platform, Pressable, ScrollView, StyleSheet, Text as RNText, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useOnScreen } from '@/hooks/useOnScreen';
import { HTML_PREVIEW_SANDBOX, injectHtmlPreviewCsp } from '@/constants/utils/code_file';
import { injectHtmlPlayerSizing, readHtmlPlayerHeight } from '@/constants/utils/html_player_sizing';
import { playableHtmlTitle } from '@/components/Coworker/coworkerArtifactTypes';

let NativeWebView = null;
if (Platform.OS !== 'web') {
  try {
    const webViewModule = require('react-native-webview');
    NativeWebView = Platform.OS === 'macos' ? require('@/components/Files/MacArtifactWebView').default
      : webViewModule.WebView || webViewModule.default;
  } catch {
    NativeWebView = null;
  }
}

const PREVIEW_RETRY_MS = [0, 800, 1600, 3200, 5000];
const INLINE_PREVIEW_MAX_HEIGHT = 480;

export default function HtmlPlayablePreview({
  name,
  sourceKey,
  loadHtml,
  onExpand,
  expandLabel = 'Open',
}) {
  const { colors } = useTheme();
  const border = colors.border_color_secondary;
  const [containerRef, isOnScreen] = useOnScreen({ rootMargin: '300px' });
  const iframeRef = useRef(null);
  const dialogRef = useRef(null);
  const loadHtmlRef = useRef(loadHtml);
  const loadedSourceRef = useRef(null);
  loadHtmlRef.current = loadHtml;
  const [html, setHtml] = useState('');
  const [status, setStatus] = useState('idle');
  const [playing, setPlaying] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState('');
  const openingRef = useRef(false);
  const [contentHeight, setContentHeight] = useState(null);
  const [stageSize, setStageSize] = useState(null);
  const sizeSession = useMemo(() => `player-${Math.random().toString(36).slice(2)}`, [sourceKey]);
  const title = playableHtmlTitle(name);
  const largePlayer = fullscreen || expanded;

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const receive = event => {
      if (largePlayer || event.source !== iframeRef.current?.contentWindow) return;
      const height = readHtmlPlayerHeight(event.data, sizeSession);
      if (height !== null) setContentHeight(height);
    };
    window.addEventListener('message', receive);
    return () => window.removeEventListener('message', receive);
  }, [sizeSession, largePlayer]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (expanded && !dialog.open) {
      dialog.showModal();
      iframeRef.current?.focus?.();
    }
    else if (!expanded && dialog.open) dialog.close();
  }, [expanded]);

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const update = () => {
      const active = document.fullscreenElement === containerRef.current;
      setFullscreen(active);
      if (active) iframeRef.current?.focus?.();
    };
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, [containerRef]);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (expanded) setExpanded(false);
      else if (document.fullscreenElement === containerRef.current) await document.exitFullscreen();
      else {
        await containerRef.current.requestFullscreen();
        // Some embedded browsers resolve the request without entering full screen.
        if (document.fullscreenElement !== containerRef.current) setExpanded(true);
      }
    } catch {
      setExpanded(true);
    }
  }, [containerRef, expanded]);

  const openArtifact = useCallback(async () => {
    if (openingRef.current) return;
    openingRef.current = true;
    setOpening(true);
    setOpenError('');
    setExpanded(false);
    if (Platform.OS === 'web' && document.fullscreenElement === containerRef.current) {
      try { await document.exitFullscreen(); } catch { /* Open is still available */ }
    }
    try {
      await onExpand?.();
    } catch {
      setOpenError('Could not open artifact. Please try again.');
    } finally {
      openingRef.current = false;
      setOpening(false);
    }
  }, [containerRef, onExpand]);

  useEffect(() => {
    setHtml('');
    setStatus('idle');
    setPlaying(false);
    setShowCode(false);
    setDownloadError('');
    setOpenError('');
    setContentHeight(null);
    loadedSourceRef.current = null;
  }, [sourceKey]);

  useEffect(() => {
    if (!isOnScreen || !loadHtmlRef.current || (loadedSourceRef.current && loadedSourceRef.current.key === sourceKey)) return undefined;
    let cancelled = false;
    setStatus('loading');
    (async () => {
      for (let i = 0; i < PREVIEW_RETRY_MS.length; i += 1) {
        if (PREVIEW_RETRY_MS[i]) {
          await new Promise((resolve) => setTimeout(resolve, PREVIEW_RETRY_MS[i]));
        }
        if (cancelled) return;
        try {
          const text = await loadHtmlRef.current();
          if (cancelled) return;
          if (!String(text || '').trim()) {
            setStatus('empty');
            return;
          }
          setHtml(String(text));
          loadedSourceRef.current = { key: sourceKey };
          setPlaying(true);
          setStatus('ready');
          return;
        } catch {
          if (cancelled) return;
        }
      }
      if (!cancelled) setStatus('error');
    })();
    return () => { cancelled = true; };
    // Loading is an output of this effect. Depending on it cancels the
    // pending request as soon as setStatus('loading') commits.
  }, [isOnScreen, sourceKey]);

  const previewHtml = useMemo(
    () => (html ? injectHtmlPlayerSizing(injectHtmlPreviewCsp(html), sizeSession) : ''),
    [html, sizeSession],
  );

  const startPlaying = useCallback(() => {
    if (status !== 'ready' || !previewHtml) return;
    setPlaying(true);
    requestAnimationFrame(() => {
      try { iframeRef.current?.focus?.(); } catch { /* iframe may not accept focus */ }
    });
  }, [previewHtml, status]);

  const canPlay = Platform.OS === 'web' || Boolean(NativeWebView);

  const downloadCode = useCallback(async () => {
    if (status !== 'ready' || downloading) return;
    setDownloading(true);
    setDownloadError('');
    const filename = String(name || 'preview.html').split(/[/\\]/).pop().replace(/[<>:"|?*]/g, '-') || 'preview.html';
    try {
      if (Platform.OS === 'web') {
        const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
        const link = document.createElement('a');
        try {
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
        } finally {
          link.remove();
          setTimeout(() => URL.revokeObjectURL(url), 4000);
        }
      } else {
        const FileSystem = await import('expo-file-system');
        const Sharing = await import('expo-sharing');
        const uri = `${FileSystem.cacheDirectory}${filename}`;
        await FileSystem.writeAsStringAsync(uri, html);
        await Sharing.shareAsync(uri, { mimeType: 'text/html', UTI: 'public.html' });
      }
    } catch {
      setDownloadError('Could not download code. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [downloading, html, name, status]);

  const player = (
    <View
      ref={containerRef}
      testID="html-playable-card"
      style={[styles.card, { borderColor: border, backgroundColor: colors.solid_color_primary }, largePlayer && styles.fullscreenCard]}
    >
      <View style={[styles.header, { borderBottomColor: border, backgroundColor: colors.solid_color_block }]}>
        <Icon name="play" size={12} color={colors.text_color_secondary} solid />
        <Text size="xs" weight="semibold" numberOfLines={1} style={styles.title}>
          {title}
        </Text>
        {Platform.OS === 'web' && (playing || largePlayer) ? (
          <>
            {playing ? <Pressable onPress={() => setPlaying(false)} accessibilityRole="button" accessibilityLabel={`Stop ${title}`} style={styles.headerAction}>
              <Text size="xxs" color="secondary" weight="medium">Stop</Text>
            </Pressable> : null}
            <Pressable onPress={toggleFullscreen} accessibilityRole="button" accessibilityLabel={largePlayer ? 'Exit full screen' : `Full screen ${title}`} style={styles.headerAction}>
              <Text size="xxs" color="secondary" weight="medium">{largePlayer ? 'Exit full screen' : 'Full screen'}</Text>
            </Pressable>
          </>
        ) : null}
        {onExpand ? (
          <Pressable
            onPress={openArtifact}
            disabled={opening}
            accessibilityRole="button"
            accessibilityLabel={`${expandLabel} ${title}`}
            style={({ hovered, pressed }) => [
              styles.headerAction,
              (hovered || pressed) && { opacity: 0.75 },
            ]}
          >
            <Text size="xxs" color="secondary" weight="medium">{opening ? 'Opening…' : expandLabel}</Text>
            <Icon name="arrow-up-right-from-square" size={11} color={colors.text_color_secondary} />
          </Pressable>
        ) : null}
      </View>
      <View style={[styles.codeActions, { borderBottomColor: border }]}>
        <Pressable disabled={status !== 'ready'} onPress={() => setShowCode((value) => !value)} accessibilityRole="button" accessibilityLabel={showCode ? `View preview ${title}` : `View code ${title}`} style={styles.headerAction}>
          <Text size="xxs" color="secondary" weight="medium">{showCode ? 'View preview' : 'View code'}</Text>
        </Pressable>
        <Pressable disabled={status !== 'ready' || downloading} onPress={downloadCode} accessibilityRole="button" accessibilityLabel={`Download code ${title}`} style={styles.headerAction}>
          <Text size="xxs" color="secondary" weight="medium">{downloading ? 'Downloading…' : 'Download code'}</Text>
          <Icon name="arrow-down-to-line" size={11} color={colors.text_color_secondary} />
        </Pressable>
      </View>
      {downloadError ? <Text size="xs">{downloadError}</Text> : null}
      {openError ? <Text size="xs" accessibilityRole="alert">{openError}</Text> : null}
      <View testID="html-playable-stage"
        onLayout={Platform.OS === 'macos' ? event => {
          const { width, height } = event.nativeEvent.layout;
          if (width > 0 && height > 0) setStageSize(previous => previous?.width === width && previous?.height === height ? previous : { width, height });
        } : undefined}
        style={[styles.stage, { backgroundColor: '#0c0f12' }, !largePlayer && styles.inlineStage, !largePlayer && contentHeight !== null && { height: Math.min(contentHeight, INLINE_PREVIEW_MAX_HEIGHT), aspectRatio: undefined }, largePlayer && styles.fullscreenStage]}>
        {(!isOnScreen && !playing) || status === 'idle' || status === 'loading' ? (
          <View style={styles.center}>
            {isOnScreen && status === 'loading' ? (
              <ActivityIndicator color="rgba(255,255,255,0.7)" />
            ) : null}
          </View>
        ) : status === 'error' || status === 'empty' || !canPlay ? (
          <View style={styles.center}>
            <Text size="s" style={styles.stageCopy}>
              {status === 'empty'
                ? 'This file is empty'
                : !canPlay
                  ? 'Open the file to play it'
                  : 'Could not load this game'}
            </Text>
            {onExpand ? (
              <Pressable
                onPress={openArtifact}
                accessibilityRole="button"
                accessibilityLabel={`Open ${title}`}
                style={styles.fallbackButton}
              >
                <Text size="xs" weight="semibold" style={styles.fallbackButtonLabel}>{expandLabel}</Text>
              </Pressable>
            ) : null}
          </View>
        ) : (
          <>
            {showCode ? (
              <ScrollView style={styles.source} contentContainerStyle={{ padding: 12 }}>
                <RNText selectable style={styles.sourceText}>{html}</RNText>
              </ScrollView>
            ) : null}
            {!playing ? null : Platform.OS === 'web' ? (
              <iframe
                ref={iframeRef}
                title={title}
                srcDoc={previewHtml}
                sandbox={HTML_PREVIEW_SANDBOX}
                allow="fullscreen; autoplay; gamepad"
                allowFullScreen
                referrerPolicy="no-referrer"
                style={{
                  ...styles.frame,
                  visibility: showCode ? 'hidden' : 'visible',
                  pointerEvents: playing ? 'auto' : 'none',
                }}
                tabIndex={playing ? 0 : -1}
              />
            ) : (
              <NativeWebView
                source={{ html: previewHtml }}
                onMessage={event => {
                  try {
                    const data = JSON.parse(event.nativeEvent.data);
                    const height = readHtmlPlayerHeight(data, sizeSession);
                    if (height !== null && !largePlayer) setContentHeight(height);
                  } catch { /* Ignore unrelated messages from the generated page. */ }
                }}
                containerStyle={Platform.OS === 'macos' && stageSize ? { flex: 0, ...stageSize } : undefined}
                style={[styles.webView, Platform.OS === 'macos' && stageSize ? { flex: 0, ...stageSize } : null, showCode ? styles.webViewIdle : null]}
                pointerEvents={playing && !showCode ? 'auto' : 'none'}
                accessibilityElementsHidden={Platform.OS === 'macos' ? showCode : undefined}
                originWhitelist={['about:blank', 'data:*', 'https://*', 'http://*']}
                javaScriptEnabled
                nestedScrollEnabled
                allowsInlineMediaPlayback
                setSupportMultipleWindows={false}
                onShouldStartLoadWithRequest={(request) => {
                  const url = String(request?.url || '');
                  if (!url || url === 'about:blank' || url.startsWith('data:text/html')) return true;
                  if (/^https?:\/\//i.test(url)) Linking.openURL(url).catch(() => {});
                  return false;
                }}
              />
            )}
            {!playing && !showCode ? (
              <Pressable
                onPress={startPlaying}
                accessibilityRole="button"
                accessibilityLabel={`Play ${title}`}
                style={styles.playHit}
              >
                <View style={styles.playButton} accessibilityElementsHidden>
                  <Icon name="play" size={22} color="#0c0f12" solid style={styles.playGlyph} />
                </View>
                <Text size="s" weight="semibold" style={styles.playLabel}>
                  Click to play
                </Text>
              </Pressable>
            ) : null}
          </>
        )}
      </View>
    </View>
  );
  // Keep one DOM subtree: moving between separate React portals remounts the
  // iframe. A dialog's top layer escapes transformed/clipped chat ancestors.
  return Platform.OS === 'web' ? (
    <dialog
      ref={dialogRef}
      aria-label={title}
      onCancel={() => setExpanded(false)}
      onClose={() => setExpanded(false)}
      style={expanded ? {
        position: 'fixed', inset: 0, width: '100vw', height: '100dvh',
        maxWidth: '100vw', maxHeight: '100dvh', margin: 0, padding: 0,
        border: 0, background: '#0c0f12', color: 'inherit',
      } : { display: 'contents' }}
    >
      {player}
    </dialog>
  ) : player;
}

const styles = StyleSheet.create({
  codeActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 16,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
  },
  source: {
    ...StyleSheet.absoluteFillObject,
  },
  sourceText: {
    color: '#e6e6e6',
    fontFamily: Platform.OS === 'ios' || Platform.OS === 'macos' ? 'Menlo' : 'monospace',
    fontSize: 12,
    lineHeight: 18,
  },
  fullscreenCard: {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    borderRadius: 0,
  },
  fullscreenStage: {
    flex: 1,
    aspectRatio: undefined,
    minHeight: 0,
  },
  inlineStage: {
    maxHeight: INLINE_PREVIEW_MAX_HEIGHT,
  },
  card: {
    width: '100%',
    minWidth: 0,
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
    maxWidth: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
  },
  title: {
    flex: 1,
    minWidth: 0,
  },
  headerAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
  },
  stage: {
    width: '100%',
    // Yoga on macOS keeps the ratio when a later style clears it with
    // undefined, so a measured height can unexpectedly widen the WebView.
    ...(Platform.OS !== 'macos' ? { aspectRatio: 16 / 10 } : {}),
    minHeight: 280,
    position: 'relative',
  },
  center: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 20,
  },
  stageCopy: {
    color: 'rgba(255,255,255,0.72)',
    textAlign: 'center',
  },
  fallbackButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  fallbackButtonLabel: {
    color: '#fff',
  },
  playHit: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    backgroundColor: 'rgba(8, 10, 12, 0.28)',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
  },
  playButton: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
    } : null),
  },
  playGlyph: {
    marginLeft: 3,
  },
  playLabel: {
    color: '#fff',
  },
  frame: {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block',
    backgroundColor: '#0c0f12',
  },
  webView: {
    width: '100%',
    height: '100%',
    backgroundColor: '#0c0f12',
  },
  webViewIdle: {
    opacity: 0,
  },
});
